import { useState } from 'preact/hooks'
import type { MediaPath } from '~/generated/media'
import { sized } from '~/utils/media'

interface VimeoEmbedProps {
	id: string
	poster?: MediaPath
	title?: string
}

// Lightweight facade: show the poster until the user clicks, then swap in the heavy
// Vimeo player iframe. Keeps project pages fast when they embed several videos.
export default function VimeoEmbed({ id, poster, title }: VimeoEmbedProps) {
	const [active, setActive] = useState(false)

	return (
		<div className="relative w-full aspect-video bg-black overflow-hidden">
			{active ? (
				<iframe
					className="absolute inset-0 w-full h-full border-none"
					src={`https://player.vimeo.com/video/${id}?autoplay=1`}
					title={title ?? 'Vimeo video'}
					allow="autoplay; fullscreen; picture-in-picture"
					allowFullScreen
				/>
			) : (
				<button
					type="button"
					onClick={() => setActive(true)}
					aria-label={`Play ${title ?? 'video'}`}
					className="group absolute inset-0 w-full h-full cursor-pointer border-none bg-no-repeat bg-center bg-cover p-0"
					style={poster ? { backgroundImage: `url(${sized(poster, 1280)})` } : undefined}
				>
					<span className="absolute inset-0 flex items-center justify-center bg-black/25 transition group-hover:bg-black/10">
						<span className="flex items-center justify-center w-16 h-16 rounded-full bg-black/60 text-white text-2xl leading-none">
							▶
						</span>
					</span>
				</button>
			)}
		</div>
	)
}

VimeoEmbed.defaultProps = {
	poster: undefined,
	title: undefined,
}
